import type { DistanceUnit as CoreDistanceUnit } from '@brightdrop/localization-core';
import type {
  OnStar,
  VehicleOverviewSearchResult,
} from '@gm-commercial/rtk-clients/assets/types';

import type { PendingVerificationVehicleStatus } from './addVehicles.model';
import {
  type AssetConnectivityStatus,
  type AssetModel,
  AssetReportType,
  type AssetRequestFieldNames,
  type AssetServiceStatus,
  type BatteryRange,
} from './asset.model';
import type {
  ConnectivityStatusType,
  Entity,
  PartialEntity,
} from './common.model';
import type { Fleet } from './fleet.model';
import type { Hub } from './hub.model';
import type { Organization } from './organization.model';
import type { FieldActionSummary } from './recalls.model';
import type { OnStarUnitState } from './vehicle.model';

export type LastCommunicated = 'Online' | 'Offline';

export enum AssetReportFieldType {
  ASSET_ID = 'id',
  ASSET_NAME = 'name',
  LICENSE_PLATE = 'licensePlate',
  FLEET_ID = 'fleetId',
  ORG_ID = 'organizationId',
  BATTERY_VALUE = "statuses.hv_battery_soc_in_pct['value']",
  BRAKE_FLUID_STATUS = "diagnostics.fluid_low_brake['value']",
  WASHER_FLUID_STATUS = "diagnostics.fluid_low_washer['value']",
  TIRE_STATUS = "diagnostics.tire_flat['value']",
  ASSET_TYPE = 'deviceType.model',
  ESTIMATED_RANGE = "statuses.estimated_range_in_km['value']",
  READY_STATUS = 'ready',
  FAULT_COUNT = 'faultCount',
  LEFT_REAR_TIRE = "statuses.tire_driver_rear_act['value']",
  LEFT_FRONT_TIRE = "statuses.tire_driver_front_act['value']",
  RIGHT_FRONT_TIRE = "statuses.tire_pass_front_act['value']",
  RIGHT_REAR_TIRE = "statuses.tire_pass_rear_act['value']",
  FRONT_EXP_TIRE = "statuses.tires_front_exp['value']",
  BATTERY_CHARGING = "statuses.charge_active['value']",
  CHARGING_STATUS = 'state.chargeState',
  CHARGING_POWER_TYPE = "statuses.ev_charging_power_type['value']",
  LAST_COMMUNICATED = 'lastCommunicated',
  FLAT_TIRE = TIRE_STATUS,
  LOW_TIRE = "diagnostics.tire_low['value']",
  SAFETY_COMPLIANT = 'state.safetyCompliant',
  ASSET_STATE_AVAILABILITY = 'state.availability',
  DIAGNOSTIC_HEALTH = 'state.diagnostics',
  //TODO: Confirm below field names with backend
  CONNECTIVITY_STATUS = 'connectivity',
  FAULT_STATUS = 'state.mil',
  LAST_UPDATED = LAST_COMMUNICATED,
  FUEL_VALUE = "statuses.fuel_level_in_pct['value']",
  OIL_LIFE_VALUE = "statuses.oil_life_in_pct['value']",
  MEDIUM_OIL_LIFE_STATUS = "diagnostics.oil_life_yellow['value']",
  LOW_OIL_LIFE_STATUS = "diagnostics.oil_life_red['value']",
  BRAKE_PAD_FRONT_VALUE = "statuses.brake_pad_life_front_in_pct['value']",
  MEDIUM_BRAKE_PAD_FRONT_STATUS = "diagnostics.brake_pad_life_front_yellow['value']",
  LOW_BRAKE_PAD_FRONT_STATUS = "diagnostics.brake_pad_life_front_red['value']",
  BRAKE_PAD_REAR_VALUE = "statuses.brake_pad_life_rear_in_pct['value']",
  MEDIUM_BRAKE_PAD_REAR_STATUS = "diagnostics.brake_pad_life_rear_yellow['value']",
  LOW_BRAKE_PAD_REAR_STATUS = "diagnostics.brake_pad_life_rear_red['value']",
  ENGINE_AIR_FILTER_VALUE = "statuses.engine_air_filter_life_in_pct['value']",
  MEDIUM_ENGINE_AIR_FILTER_STATUS = "diagnostics.engine_air_filter_yellow['value']",
  LOW_ENGINE_AIR_FILTER_STATUS = "diagnostics.engine_air_filter_red['value']",
  CONNECTIVITY_BOARDING_STATUS = "statuses.vehicleDataConnectivityBoardingStatus['value']",
  CONNECTIVITY_PRIMARY_OWNERSHIP_STATUS = 'organizationPrimaryOwnershipStatus',
  ONSTAR_SUPPORT_STATUS = 'onStar.supportStatus',
  ONSTAR_CONNECTION_STATUS = 'connectivity.onStar.connectionStatus',
  LOW_DIESEL_EXHAUST_FLUID = "diagnostics.diesel_exhaust_red['value']",
  GOOD_DIESEL_EXHAUST_FLUID = "diagnostics.diesel_exhaust_green['value']",
  RECALLS_STATUS = 'fieldActionTypes',
  AVAILABILITY_STATUS = 'availability',
  CRITICAL_DIAGNOSTICS = 'state.diagnosticsCritical',
  WARNING_DIAGNOSTICS = 'state.diagnosticsImpaired',
  CRITICAL_PROGNOSTICS = 'state.prognosticsCritical',
  VEHICLE_PROPULSION = 'vehiclePropulsionModel.vehiclePropulsion',
  LOCATION_MASKING = "statuses.location_masking['value']",
}
export enum AssetComplianceFieldType {
  SAFETY_COMPLIANT = 'safetyCompliance.value',
  CHARGING_COMPLIANT = 'chargingCompliance.value',
}
export const ASSET_REPORT_FIELD_NAMES: AssetRequestFieldNames = {
  id: AssetReportFieldType.ASSET_ID,
  name: AssetReportFieldType.ASSET_NAME,
  asset: AssetReportFieldType.ASSET_NAME,
  licensePlate: AssetReportFieldType.LICENSE_PLATE,
  organizationId: AssetReportFieldType.ORG_ID,
  fleetId: AssetReportFieldType.FLEET_ID,
  type: AssetReportFieldType.ASSET_TYPE,
  ready: AssetReportFieldType.READY_STATUS,
  connectivity: AssetReportFieldType.CONNECTIVITY_STATUS,
  battery: AssetReportFieldType.BATTERY_VALUE,
  brakeFluid: AssetReportFieldType.BRAKE_FLUID_STATUS,
  washerFluid: AssetReportFieldType.WASHER_FLUID_STATUS,
  tires: AssetReportFieldType.TIRE_STATUS,
  estimatedRange: AssetReportFieldType.ESTIMATED_RANGE,
  faultCount: AssetReportFieldType.FAULT_COUNT,
  leftRearTire: AssetReportFieldType.LEFT_REAR_TIRE,
  leftFrontTire: AssetReportFieldType.LEFT_FRONT_TIRE,
  rightFrontTire: AssetReportFieldType.RIGHT_FRONT_TIRE,
  rightRearTire: AssetReportFieldType.RIGHT_REAR_TIRE,
  batteryCharging: AssetReportFieldType.BATTERY_CHARGING,
  safetyCompliant: AssetReportFieldType.SAFETY_COMPLIANT,
  milState: AssetReportFieldType.FAULT_STATUS,
  lastUpdated: AssetReportFieldType.LAST_UPDATED,
  availability: AssetReportFieldType.ASSET_STATE_AVAILABILITY,
  diagnosticHealth: AssetReportFieldType.DIAGNOSTIC_HEALTH,
  fuel: AssetReportFieldType.FUEL_VALUE,
  oilLife: AssetReportFieldType.OIL_LIFE_VALUE,
  brakePadFront: AssetReportFieldType.BRAKE_PAD_FRONT_VALUE,
  brakePadRear: AssetReportFieldType.BRAKE_PAD_REAR_VALUE,
  engineAirFilter: AssetReportFieldType.ENGINE_AIR_FILTER_VALUE,
  connectivityBoardingStatus: AssetReportFieldType.CONNECTIVITY_BOARDING_STATUS,
  fieldActionTypeStatus: AssetReportFieldType.RECALLS_STATUS,
  availabilityStatus: AssetReportFieldType.AVAILABILITY_STATUS,
  propulsionType: AssetReportFieldType.VEHICLE_PROPULSION,
  locationMasking: AssetReportFieldType.LOCATION_MASKING,
};

export enum BrakePadStatus {
  GOOD = 'good',
  IMPAIRED = 'impaired',
  CRITICAL = 'critical',
}
export enum BatterySocStatus {
  HIGH_CHARGE = 'info1',
  MEDIUM_CHARGE = 'info0',
  LOW_CHARGE = 'impaired',
  CRITICAL_CHARGE = 'critical',
}

export enum ReadyStatus {
  READY = 'ready',
  NOT_READY = 'not_ready',
  UNKNOWN = 'unknown',
}
export enum BatteryChargingStatus {
  CHARGING = 'charging',
  IDLE = 'idle',
  COMPLETED = 'completed',
  ABORTED = 'aborted',
  UNPLUGGED = 'unplugged',
  PLUGGED = 'plugged',
  NO_DATA = 'no_data',
  UNKNOWN = 'unknown',
}

/**
 * @deprecated from v 0.7 - use ChargerType instead
 */
export enum PowerType {
  AC = 'AC',
  DC = 'DC',
  UNKNOWN = 'unknown',
}

export enum ChargerType {
  LEVEL_1 = 'level_1',
  LEVEL_2 = 'level_2',
  DC = 'dc',
  UNKNOWN = 'unknown',
}

export enum EstimatedRangeStatus {
  VERY_SHORT_RANGE = 'very_short_range', // 0-50 miles
  SHORT_RANGE = 'short_range', // 51-149 miles
  MEDIUM_RANGE = 'medium_range', // 150-299 miles
  LONG_RANGE = 'long_range', // 300+ miles
}

export enum ReportDiagnosticState {
  GOOD = 'good',
  OK = 'ok',
  IMPAIRED = 'impaired',
  CRITICAL = 'critical',
  NO_DATA = 'no_data',
  CLEAR = 'clear',
  UNKNOWN = 'unknown',
}

export enum SafetyCompliance {
  COMPLIANT = 'compliant',
  NON_COMPLIANT = 'non_compliant',
}

export enum FuelStatus {
  HIGH_FUEL = 'high',
  MEDIUM_FUEL = 'medium',
  LOW_FUEL = 'low',
  CRITICAL_FUEL = 'critical',
}
export type TiresStatus = {
  pressureInPSI: number;
  state: ReportDiagnosticState;
};
export enum OilLifeStatus {
  GOOD = 'good',
  IMPAIRED = 'impaired',
  CRITICAL = 'critical',
}

export enum VehiclePropulsion {
  EV = 'EV',
  ICE = 'ICE',
  HEV = 'HEV',
  PHEV = 'PHEV',
}
export enum LastCommunicatedStatus {
  WITHIN_1HR = 'within_1hr',
  WITHIN_1D = 'within_1day',
  WITHIN_1W = 'within_1week',
  WITHIN_M1W = 'within_moreThanWeek',
}
export enum LastIgnitionStatus {
  WITHIN_1HR = 'within_1hr',
  WITHIN_1D = 'within_1day',
  WITHIN_1W = 'within_1week',
  WITHIN_M1W = 'within_moreThanWeek',
}
export enum DiagnosticDTCType {
  TIRE = 'tire',
  BRAKE_FLUID = 'brake_fluid',
  ENGINE_AIR_FILTER = 'engine_air_filter',
  WASHER_FLUID = 'washer_fluid',
  BRAKE_PAD = 'brake_pad',
  OIL_LIFE = 'oil_life',
  FUEL_LEVEL = 'fuel_level',
  BATTERY = 'battery',
  DIESEL_EXHAUST_FLUID = 'diesel_exhaust',
  AIRBAG = 'airbag',
  ABS = 'antilock_braking',
  ONSTAR = 'onstar',
  ENGINE_AND_TRANSMISSION = 'engine_and_transmission',
  EMISSIONS = 'emissions',
  STABILITY = 'stability_control',
  ELECTRIC_DRIVE_UNIT = 'electric_drive',
  ELECTRIC_LAMP = 'electric_lamps',
  BATTERY_SYSTEM = 'high_voltage_battery',
  TWELVE_VOLT_BATTERY = 'twelve_volt_battery',
  TWELVE_VOLT_BATTERY_ISSUE = 'twelve_volt_battery_issue',
}

/**
 * 12-volt battery prognostics arrive as five SAC subtypes. They are never rendered
 * individually - see `collapseTwelveVoltBatteryDiagnostics`.
 */
export enum TwelveVoltBatterySacType {
  SAC001 = 'twelve_volt_battery_SAC001',
  SAC002 = 'twelve_volt_battery_SAC002',
  SAC003 = 'twelve_volt_battery_SAC003',
  SAC004 = 'twelve_volt_battery_SAC004',
  SAC005 = 'twelve_volt_battery_SAC005',
}

const TWELVE_VOLT_BATTERY_SAC_TYPES_BY_LOWERCASE: ReadonlyMap<
  string,
  TwelveVoltBatterySacType
> = new Map(
  Object.values(TwelveVoltBatterySacType).map((type) => [
    type.toLowerCase(),
    type,
  ])
);

/**
 * Resolves a reported subtype to its canonical enum member, so downstream code never holds a
 * value that is typed as the enum but cased differently.
 */
export const toTwelveVoltBatterySacType = (
  type?: string
): TwelveVoltBatterySacType | undefined =>
  type
    ? TWELVE_VOLT_BATTERY_SAC_TYPES_BY_LOWERCASE.get(type.toLowerCase())
    : undefined;

/**
 * Matched case-insensitively: an unmatched subtype falls through to the renderer as a
 * missing icon and a raw translation key, which is too costly to risk on the casing.
 * `type` is optional because the API marks it so, and a throw here takes out the whole list.
 */
export const isTwelveVoltBatterySacType = (type?: string): boolean =>
  type
    ? TWELVE_VOLT_BATTERY_SAC_TYPES_BY_LOWERCASE.has(type.toLowerCase())
    : false;

type OmittedKeys = 'fuel_level' | 'battery';

export type AllowedDiagnosticDTCType = Exclude<DiagnosticDTCType, OmittedKeys>;

export type EstimatedRangedInKM = {
  value: number;
  unitOfMeasure: CoreDistanceUnit;
};
export type VehicleDetailsType = {
  make?: string;
  year?: string;
  model?: string;
  propulsion?: VehiclePropulsion;
  peg?: string | null;
  merchandisingModel?: string | null;
  rpoCodes?: Array<string> | null;
};
export interface ApiDiagnostic {
  state: ReportDiagnosticState;
  type: DiagnosticDTCType | TwelveVoltBatterySacType;
  deviceTimestamp: string;
  severityRank?: number;
}
export interface Diagnostic {
  state: ReportDiagnosticState;
  type: AllowedDiagnosticDTCType;
  deviceTimestamp: string;
  originalAPIDiagnosticType?: DiagnosticDTCType | TwelveVoltBatterySacType;
}
export interface AdvisorVehicleListItem extends Entity {
  organization?: PartialEntity<Organization>;
  hub?: PartialEntity<Hub>;
  fleet?: PartialEntity<Fleet>;
  licensePlate?: string;
  connectivityBoardingStatus?: ConnectivityStatusType;
  propulsion?: VehiclePropulsion;
  vehicleType?: VehicleDetailsType;
  fan?: number;
  lastCommunicated?: {
    deviceTimestamp: string | undefined;
    status: LastCommunicated | undefined;
  };
}

export interface AssetReportItem extends Entity {
  /** @deprecated This field will be removed in a future version */
  type: AssetModel;
  organization?: PartialEntity<Organization>;
  hub?: PartialEntity<Hub>;
  fleet?: PartialEntity<Fleet>;
  connectivity?: AssetConnectivityStatus;
  lastUpdated?: string;
  ready?: boolean;
  faultCount?: number;
  battery?: Partial<{
    isCharging: boolean;
    range: BatteryRange;
    value: number;
    estimatedRange?: EstimatedRangedInKM;
    lastUpdated?: string;
    chargingPowerType?: ChargerType;
    chargeCompletionTime?: string;
  }>;
  brakeFluid?: ReportDiagnosticState;
  washerFluid?: ReportDiagnosticState;
  leftRearTire?: TiresStatus;
  leftFrontTire?: TiresStatus;
  rightFrontTire?: TiresStatus;
  rightRearTire?: TiresStatus;
  estimatedRange?: EstimatedRangedInKM;
  tires?: ReportDiagnosticState;
  safetyCompliant?: boolean;
  milState?: ReportDiagnosticState;
  diagnosticHealth?: ReportDiagnosticState | null;
  availability?: AssetServiceStatus;
  chargingStatus?: BatteryChargingStatus;
  /** @deprecated from v 0.7 - use battery.chargingPowerType instead */
  powerType?: PowerType;
  targetSOC?: {
    value?: number;
    estimatedRange?: EstimatedRangedInKM;
  };
  minutesToTargetSOC?: {
    value?: number;
    deviceTimestamp?: string;
  };
  fuel?: {
    value: number | undefined;
    state: ReportDiagnosticState | undefined;
  };
  licensePlate?: string;
  oilLife?: {
    value: number | undefined;
    state: ReportDiagnosticState | undefined;
  };
  brakePadRear?: {
    value: number | undefined;
    state: ReportDiagnosticState | undefined;
  };
  brakePadFront?: {
    value: number | undefined;
    state: ReportDiagnosticState | undefined;
  };
  engineAirFilter?: {
    value: number | undefined;
    state: ReportDiagnosticState | undefined;
  };
  connectivityBoardingStatus?: ConnectivityStatusType;
  vehicleType?: VehicleDetailsType;
  lastCommunicated?: {
    deviceTimestamp: string | undefined;
    status: LastCommunicated | undefined;
  };
  lastIgnition?: string | undefined;
  diagnostics?: Diagnostic[];
  fieldActionSummary?: FieldActionSummary;
  imageUrl: string;
  diagnosticsCritical?: boolean | null;
  diagnosticsImpaired?: boolean | null;
  /** 12-volt prognostics are always critical, so there is no impaired counterpart. */
  prognosticsCritical?: boolean | null;
  driver?: {
    firstName: string;
    lastName: string;
    email: string;
  };
  lastOnboardedDate?: string | null;
  onStarUnitState?: OnStarUnitState | null;
  isTcpsAccepted?: boolean | null;
  //TODO:Update the field when api starts retuning the field for verification pending status
  reviewReason?: PendingVehicleReviewReason | null;
  verificationPendingStatus?: PendingVerificationVehicleStatus | null;
  locationMasking?: boolean | null;
  userAction?: VehicleOverviewSearchResult['userAction'] | null;
}

export enum PendingVehicleReviewReason {
  // The following codes can only be retried or declined
  VEHICLE_ACCOUNT_TYPE_NOT_SUPPORTED = 'VEHICLE_ACCOUNT_TYPE_NOT_SUPPORTED',
  ONSTAR_ACCOUNT_NOT_FOUND = 'ONSTAR_ACCOUNT_NOT_FOUND',
  ONSTAR_ACCOUNT_NOT_ACTIVE = 'ONSTAR_ACCOUNT_NOT_ACTIVE',
  ONSTAR_ACCOUNT_NO_OWNER_EMAIL = 'ONSTAR_ACCOUNT_NO_OWNER_EMAIL',
  ONSTAR_FLEET_ACCOUNT_NO_FAN = 'ONSTAR_FLEET_ACCOUNT_NO_FAN',
  ONSTAR_NOT_AVAILABLE = 'ONSTAR_NOT_AVAILABLE',

  // The following codes can be approved, retried, or declined
  UNEXPECTED = 'UNEXPECTED',
  NO_MATCHING_FAN = 'NO_MATCHING_FAN',
  NO_MATCHING_ONSTAR_ACCOUNT_EMAIL = 'NO_MATCHING_ONSTAR_ACCOUNT_EMAIL',
  ONSTAR_GEN_NOT_SUPPORTED = 'ONSTAR_GEN_NOT_SUPPORTED',
  VEHICLE_POWERTRAIN_NOT_SUPPORTED = 'VEHICLE_POWERTRAIN_NOT_SUPPORTED',
  ONSTAR_NOT_EQUIPPED = 'ONSTAR_NOT_EQUIPPED',
  ONSTAR_NOT_SUPPORTED = 'ONSTAR_NOT_SUPPORTED',
}

export interface PendingVehicleItem extends Entity {
  createdOn: string;
  fan: number;
  onStar: OnStar;
  organizationId: string;
  reviewReason?: PendingVehicleReviewReason;
  sessionId: string;
  status: string;
  vin: string;
  vehicleAccountType?: string;
  vehicleImageSrc?: string;
  vehicleMake?: string;
  vehicleModel?: string;
  vehicleYear?: string;
}
